import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    type Asset,
    type PatientCloudFile,
    fetchPatientCloudFiles
} from '../services/patientFiles';
import { SIGNED_URL_GC_TIME_MS } from '../providers/queryClient';
import { PATIENT_ASSETS_KEY } from './queryKeys';

const ASSETS_STALE_TIME_MS = 5 * 60 * 1000;

/** GCS (via `/cloud-files`) is the sole source of patient files, including chat uploads. */
const toAssetMap = (gcsData: PatientCloudFile[]): Record<string, Asset> => {
    const assets: Record<string, Asset> = {};

    for (const patientFile of gcsData) {
        const { file } = patientFile;
        const assetId = `gcs-${patientFile.id}`;
        assets[assetId] = {
            name: file.originalFilename,
            assetId,
            fileType: file.mimeType,
            publicUrl: '',
            createdAt: file.createdAt,
            source: 'gcs',
            patientFileId: patientFile.id,
            uploadedBy: patientFile.uploadedBy,
            thumbUrl: patientFile.thumbUrl ?? null,
            storageCategory: patientFile.fileType ?? null,
            metadata: patientFile.metadata ?? null,
            internal: patientFile.internal ?? false
        };
    }

    return assets;
};

interface UsePatientAssetsOptions {
    enabled?: boolean;
}

interface UsePatientAssetsResult {
    assets: Record<string, Asset>;
    isLoading: boolean;
    error: unknown;
    refresh: () => Promise<void>;
}

const fetchAssets = async (patientId: string): Promise<Record<string, Asset>> => {
    const gcsData = await fetchPatientCloudFiles(patientId);
    return toAssetMap(gcsData);
};

export function usePatientAssets(
    patientId: string | undefined,
    options: UsePatientAssetsOptions = {}
): UsePatientAssetsResult {
    const queryClient = useQueryClient();
    const enabled = (options.enabled ?? true) && !!patientId;

    const { data, isLoading, error } = useQuery<Record<string, Asset>>({
        queryKey: PATIENT_ASSETS_KEY(patientId ?? 'unknown'),
        queryFn: () => fetchAssets(patientId!),
        enabled,
        staleTime: ASSETS_STALE_TIME_MS,
        gcTime: SIGNED_URL_GC_TIME_MS
    });

    const refresh = useCallback(async () => {
        if (!patientId) return;
        await queryClient.invalidateQueries({ queryKey: PATIENT_ASSETS_KEY(patientId) });
    }, [queryClient, patientId]);

    return {
        assets: data ?? {},
        isLoading,
        error,
        refresh
    };
}

export function usePatientAssetsCount(
    patientId: string | undefined,
    options: UsePatientAssetsOptions = {}
): number {
    const enabled = (options.enabled ?? true) && !!patientId;

    const { data } = useQuery<Record<string, Asset>, unknown, number>({
        queryKey: PATIENT_ASSETS_KEY(patientId ?? 'unknown'),
        queryFn: () => fetchAssets(patientId!),
        enabled,
        staleTime: ASSETS_STALE_TIME_MS,
        gcTime: SIGNED_URL_GC_TIME_MS,
        select: (assets) => Object.keys(assets).length
    });

    return data ?? 0;
}
